// Client-portal navigation tracker.
//
// Mounted between ProtectedRoute("client") and Layout in App.jsx so it only
// sees client-role navigation — admin pages never call trackActivity. On
// every location change, waits 150ms (debounce for redirect chains), then
// POSTs a { event_type: 'nav', path } to /me/activity/track. If the tab is
// closed/hidden before the debounce fires, a pagehide listener flushes the
// pending event immediately (trackActivity uses keepalive so the request
// survives teardown).
//
// The Outlet at the end passes rendering down to Layout → the actual page,
// so this component adds zero visible chrome — it's purely a side-effect
// wrapper.

import { useEffect, useRef } from 'react'
import { useLocation, Outlet } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { trackActivity } from '../lib/api'

// Routes that aren't "the user on the portal yet" — pre-auth screens.
// SKIP_PREFIXES is checked with startsWith so /login and /set-password
// stay silent even during a redirect chain that briefly lands there.
const SKIP_PREFIXES = ['/login', '/set-password', '/admin']

// Every client-scoped URL follows /account/<uuid>/<tab>, so we can pull the
// client_id out of the pathname and pass it into the activity payload —
// otherwise every log row is stuck at client_name=NULL and the admin's
// Client column is a useless em-dash. The backend re-verifies the id
// against the caller's allowlist (via _resolve_client_id) so a spoofed
// value gets silently NULLed rather than trusted; we're just passing a
// hint so the join can happen.
const CLIENT_ID_FROM_PATH = /^\/account\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$)/i

function extractClientId(pathname) {
  const m = pathname.match(CLIENT_ID_FROM_PATH)
  return m ? m[1] : undefined
}

export default function ClientActivityTracker() {
  const location = useLocation()
  const { user, role } = useAuth()
  const timer = useRef(null)
  const pendingPath = useRef(null)
  const lastFiredPath = useRef(null)

  useEffect(() => {
    if (!user || role !== 'client') return
    const path = location.pathname
    if (SKIP_PREFIXES.some(p => path === p || path.startsWith(p + '/'))) return

    // Dedupe: React StrictMode double-mounts the effect in dev, and other
    // re-renders (auth object identity changing) can re-run this effect
    // with the same pathname. If we already logged this exact path last,
    // don't log it again.
    if (lastFiredPath.current === path) return

    const client_id = extractClientId(path)

    // Debounce: React StrictMode double-mounts effects in dev, and any
    // login redirect chain (/login → / → /account → /account/<id>/overview)
    // fires the effect four times inside a few ms. 150ms is long enough to
    // coalesce those bursts (~50ms) but short enough that a fast tab-close
    // still lets the timer fire (and pagehide catches anything shorter).
    if (timer.current) clearTimeout(timer.current)
    pendingPath.current = path
    timer.current = setTimeout(() => {
      trackActivity({ event_type: 'nav', path, client_id })
      lastFiredPath.current = path
      pendingPath.current = null
      timer.current = null
    }, 150)

    // If the tab is being hidden/closed before the debounce fires, flush
    // the pending event synchronously. trackActivity's keepalive fetch is
    // designed for exactly this teardown window.
    const flushOnHide = () => {
      if (timer.current && pendingPath.current) {
        clearTimeout(timer.current)
        const p = pendingPath.current
        trackActivity({ event_type: 'nav', path: p, client_id: extractClientId(p) })
        lastFiredPath.current = p
        pendingPath.current = null
        timer.current = null
      }
    }
    window.addEventListener('pagehide', flushOnHide)

    return () => {
      window.removeEventListener('pagehide', flushOnHide)
      if (timer.current) {
        clearTimeout(timer.current)
        timer.current = null
      }
    }
  }, [location.pathname, user, role])

  return <Outlet />
}
